import { createContext, useMemo } from 'react';

export const SYSTEM_VERSION = 'v18.27.73-KPI-APPLICABILITY-PARITY';
export const LAST_STABLE_CHECKPOINT = 'v18.26.29-SYNC-ENGINE-PHASE-2E-FORMAT-PARITY';
export const APP_VERSION = "v18.27.73-KPI-APPLICABILITY-PARITY";
export const APP_DATE = '21 de Julio, 2026';

/**
 * normalizeModuleName: Convierte nombres de módulos a un formato canónico (lowercase, sin acentos, sin mojibake).
 * v18.12.65: Blindaje exhaustivo contra mojibake y variaciones de acentos.
 */
export const normalizeModuleName = (val: string | null | undefined): string => {
    if (!val) return "";
    
    let s = String(val)
        .replace(/Planeaci\u00c3\u0192\u00c2\u00b3n/g, "Planeacion")
        .replace(/Configuraci\u00c3\u0192\u00c2\u00b3n/g, "Configuracion")
        .replace(/Sincronizaci\u00c3\u0192\u00c2\u00b3n/g, "Sincronizacion")
        .replace(/\u00c3\u0192\u00c2\u00b3/g, "o")
        .replace(/\u00c3\u0192\u00c2\u00a1/g, "a")
        .replace(/\u00c3\u0192\u00c2\u00a9/g, "e")
        .replace(/\u00c3\u0192\u00c2\u00ad/g, "i")
        .replace(/\u00c3\u0192\u00c2\u00ba/g, "u")
        .replace(/\u00c3\u0192\u00c2\u00b1/g, "n");

    return s.normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .replace(/\s+/g, " ")
            .trim();
};

/**
 * safeModuleEquals: Comparación determinista de módulos.
 */
export const safeModuleEquals = (a: string | null | undefined, b: string | null | undefined): boolean => {
    return normalizeModuleName(a) === normalizeModuleName(b);
};

export const parseNumberSafe = (val: any, fallback: number = 0): number => {
  if (val === null || val === undefined || val === '') return fallback;
  const parsed = Number(val);
  return isNaN(parsed) ? fallback : parsed;
};

export const normalize = (str: string) => {
  if (!str) return '';
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().trim();
};

export const getIndicatorMetrics = (ind: any, periodIdx: number, useCurrentPeriodOnly: boolean = false) => {
  if (!ind || !ind.history) return { pct: 0, meta: 0, real: 0, isActive: false, audit: 'No history' };
  const history = Array.isArray(ind.history) ? ind.history : [];
  
  // REGLA: Si se especifica un periodo, usamos ese como límite. 
  // No incluimos meses futuros al periodo seleccionado.
  const canonicalPeriodIdx = periodIdx;
  
  const targetHistory = useCurrentPeriodOnly 
    ? (history[periodIdx] ? [history[periodIdx]] : [])
    : history.slice(0, canonicalPeriodIdx + 1);
  
  // REGLA: Un indicador es elegible SOLO si tiene meta > 0 dentro del periodo evaluado.
  // Para promedios, evaluamos si hay alguna meta > 0 en el rango.
  const eligibleHistory = targetHistory.filter((h: any) => parseNumberSafe(h.meta, 0) > 0);
  
  const rawMeta = targetHistory.reduce((acc, h) => acc + parseNumberSafe(h.meta, 0), 0);
  const rawReal = targetHistory.reduce((acc, h) => acc + parseNumberSafe(h.real, 0), 0);
  
  let metaVal = rawMeta;
  let realVal = rawReal;

  if (ind.calculo === 'Promedio' && eligibleHistory.length > 0) {
    metaVal = rawMeta / eligibleHistory.length;
    realVal = rawReal / eligibleHistory.length;
  } else if (ind.calculo === 'Ultimo' && eligibleHistory.length > 0) {
    const last = eligibleHistory[eligibleHistory.length - 1];
    metaVal = parseNumberSafe(last.meta, 0);
    realVal = parseNumberSafe(last.real, 0);
  }

  let pct = 0;
  if (ind.tipo === 'Minimizar') {
    // Si meta > 0 y real = 0, es 100%.
    pct = realVal > 0 ? (metaVal / realVal) * 100 : (metaVal > 0 ? 100 : 0);
  } else {
    pct = metaVal > 0 ? (realVal / metaVal) * 100 : 0;
  }

  // Blindaje: Meta 0 = 0% cumplimiento.
  if (metaVal <= 0) pct = 0;

  const finalPct = Math.min(Math.max(0, Math.round(pct)), 100);

  return {
    pct: finalPct,
    meta: metaVal,
    real: realVal,
    isActive: metaVal > 0, // REGLA CANONICA: Solo si meta > 0
    note: targetHistory[targetHistory.length - 1]?.note || ""
  };
};

export const MONTHS = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
export const MONTHS_LABELS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
export const MONTHS_FULL = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];

export const monthToNum = (m: string) => {
  const idx = MONTHS_FULL.indexOf(m.toUpperCase());
  if (idx !== -1) return idx + 1;
  const idxShort = MONTHS.indexOf(m.toUpperCase().substring(0, 3));
  return idxShort !== -1 ? idxShort + 1 : 1;
};

/**
 * REGLA CANÃ“NICA: MESES VENCIDOS
 * Retorna el índice del mes a evaluar, excluyendo matemáticamente el mes actual en curso.
 */
export const getElapsedCompletedMonths = (targetMonthIdx?: number): number => {
    const currentYear = new Date().getFullYear();
    const currentMonthIdx = new Date().getMonth();
    
    let elapsedCompletedMonthIdx = -1;
    if (currentYear > 2026) elapsedCompletedMonthIdx = 11;
    else if (currentYear === 2026) elapsedCompletedMonthIdx = currentMonthIdx - 1;
    
    if (targetMonthIdx !== undefined) {
        return Math.min(targetMonthIdx, elapsedCompletedMonthIdx);
    }
    return elapsedCompletedMonthIdx;
};

export interface SystemSettings {
  threshold_green: number;
  threshold_amber: number;
  weight_comisiones: number;
  weight_comites: number;
  weight_consejerias: number;
  weight_dimension_plans: number;
  weight_dimension_kpis: number;
  plan_weight: number;
  kpi_weight: number;
  stale_1_days?: number;
  stale_2_days?: number;
  stale_3_days?: number;
  stale_penalty?: number;
  stale_penalty_enabled?: boolean;
  quarterly_hardening?: boolean;
  operational_toggles_enabled?: boolean;
}

export const DEFAULT_SETTINGS: SystemSettings = Object.freeze({
  threshold_green: 90,
  threshold_amber: 75,
  weight_comisiones: 50,
  weight_comites: 30,
  weight_consejerias: 20,
  weight_dimension_plans: 30,
  weight_dimension_kpis: 70,
  plan_weight: 30,
  kpi_weight: 70,
  stale_1_days: 1,
  stale_2_days: 2,
  stale_3_days: 3,
  stale_penalty: 10,
  stale_penalty_enabled: true,
  quarterly_hardening: false,
  operational_toggles_enabled: true
});

export const SettingsContext = createContext<SystemSettings>(DEFAULT_SETTINGS);

export const useRole = (user: any) => {
  return useMemo(() => {
    if (!user) return { isSA: false, isND: false, isVP: false, isUserAdmin: false, isPresident: false, isGlobal: false, isInstance: false, isEditor: false, isVisor: false, canApproveAsVP: false, isAdmin: false, isSuperAdmin: false };
    const role = user.role || '';
    const name = user.name || '';
    const isSA = role === 'SUPER_ADMIN';
    const isND = role === 'DIRECTOR_NACIONAL';
    const isVP = role === 'VICE_PRESIDENT' || role === 'VICEPRESIDENTE' || (role === 'ADMINISTRADOR' && user.org_scope === 'VP') || (role === 'VISOR' && !!user.vicepresidency_id);
    const isUserAdmin = role === 'SUPER_ADMIN' || role === 'ADMINISTRADOR';
    const isPresident = role === 'COMMISSION_PRESIDENT' || role === 'PRESIDENTE' || role === 'INSTANCIA_PRESIDENTE';
    const isInstance = user.org_scope === 'INSTANCE' || !!user.instance_id || !!user.commission_id;
    const isGlobal = isSA || isND || user.org_scope === 'GLOBAL';
    
    // Formatos canónicos VISOR/EDITOR
    const isEditor = isSA || isND || (isVP && role !== 'VISOR') || isUserAdmin || role === 'EDITOR';
    const isVisor = role === 'VISOR' || role === 'VIEWER' || role === 'VIEW_ONLY';
    
    const isAdmin = isUserAdmin;
    const isSuperAdmin = isSA;
    const canApproveAsVP = isSA || isND || (isVP && !!user.vicepresidency_id);

    return { isSA, isND, isVP, isUserAdmin, isPresident, isGlobal, isInstance, isEditor, isVisor, canApproveAsVP, isAdmin, isSuperAdmin };
  }, [user]);
};

/**
 * REGLA DE NEGOCIO: canAvalVpPlan (v18.10.92)
 * Determina si un usuario puede avalar un plan de trabajo.
 * 1. Super Admin y Director Nacional pueden avalar TODO.
 * 2. Un VP puede avalar solo planes dentro de su alcance (vicepresidency_id).
 * 3. Un VISOR puro nunca puede avalar (incluso si tiene scope de visualización).
 */
export const canAvalVpPlan = (user: any, plan: any): boolean => {
  if (!user || !plan) return false;

  const role = user.role || '';
  const isSA = role === 'SUPER_ADMIN';
  const isND = role === 'DIRECTOR_NACIONAL';
  const isAdminGlobal = role === 'ADMINISTRADOR' && user.org_scope === 'GLOBAL';

  // 1. ACCESO TOTAL: Super Admin, Director Nacional y Admins Globales
  if (isSA || isND || isAdminGlobal) return true;

  // 2. RESTRICCIÃ“N VISOR: Un visor puro nunca avala (v18.10.92 strict rule)
  // Si es VISOR pero tiene vicepresidency_id, se considera VP para efectos de aval si la lógica useRole así lo define,
  // pero la regla del usuario dice "Un VISOR puro: no debe avalar". 
  // Interpretamos "visor puro" como alguien con rol VISOR que NO es un VP delegado.
  if (role === 'VISOR' && !user.vicepresidency_id) return false;

  // 3. VICEPRESIDENTE: Solo avala lo que está en su vicepresidencia
  const isVP = role === 'VICE_PRESIDENT' || role === 'VICEPRESIDENTE' || (role === 'ADMINISTRADOR' && user.org_scope === 'VP') || (role === 'VISOR' && !!user.vicepresidency_id);
  
  if (isVP) {
    const userVpId = user.vicepresidency_id;
    const planVpId = plan.vicepresidency_id || plan.gov_commissions?.vicepresidency_id;
    
    // Si el usuario tiene una VP asignada y el plan coincide, permite avalar
    if (userVpId && planVpId === userVpId) return true;
  }

  return false;
};

// --- LOGICA DE ACCESO CANONICA (v18.9.91) ---

export const ALL_MODULES = ["Dashboard", "Talento", "Estructura", "Planeacion", "Seguimiento", "Reportes", "Memoria", "Sincronizacion", "Usuarios", "Configuracion", "OrgStructure", "Escritorio IA", "Control Operativo"];
const DEFAULT_ROLE_MODULES = ALL_MODULES.filter(m => m !== 'Control Operativo');
const RESTRICTED_FALLBACK_MODULES = ["Dashboard", "Seguimiento", "Reportes"];

export const prefixMap: Record<string, string> = {
    'PLANEACION': 'Planeacion',
    'SEGUIMIENTO': 'Seguimiento',
    'REPORTES': 'Reportes',
    'MEMORIA': 'Memoria',
    'SINCRONIZACION': 'Sincronizacion',
    'CONFIGURACION': 'Configuracion',
    'TALENTO': 'Talento',
    'ESTRUCTURA': 'Estructura',
    'USUARIOS': 'Usuarios',
    'CONTROL_OPERATIVO': 'Control Operativo',
    'CONTROLOPERATIVO': 'Control Operativo'
};

/**
 * Sanea y normaliza la lista de módulos accesibles para un usuario.
 * Purga duplicados y corrige errores de codificación heredados.
 */
export const sanitizeAccessibleModules = (raw: string[]): string[] => {
    const clean: string[] = [];
    const seen = new Set<string>();
    if (!Array.isArray(raw)) return [];

    raw.forEach(entry => {
        if (!entry) return;
        
        // Limpieza de caracteres basura comunes (Sanitización Forense)
        const cleanEntry = entry.replace(/[^a-zA-Z0-9\u00E1\u00E9\u00ED\u00F3\u00FA\u00C1\u00C9\u00CD\u00D3\u00DA\u00F1\u00D1 \u2022:]/g, '').trim();
        if (!cleanEntry) return;

        const parts = cleanEntry.split(':');
        const modRaw = normalize(parts[0]);
        const permRaw = parts[1] ? normalize(parts[1]) : 'EDITOR';

        if (permRaw === 'OFF') return;

        // Buscar coincidencia canónica
        let canonicalMod = ALL_MODULES.find(m => safeModuleEquals(m, modRaw));
        
        if (!canonicalMod) {
            // Buscar por prefijo en el mapa
            for (const prefix in prefixMap) {
                if (modRaw.startsWith(prefix)) {
                    canonicalMod = prefixMap[prefix];
                    break;
                }
            }
        }

        if (canonicalMod) {
            const finalPerm = permRaw.includes('VISOR') ? 'VISOR' : 'EDITOR';
            const key = `${canonicalMod}:${finalPerm}`;
            const normKey = normalize(canonicalMod);
            if (!seen.has(normKey)) {
                clean.push(key);
                seen.add(normKey);
            }
        }
    });

    return clean;
};

/**
 * Parse accessible_modules:
 * - Formato nuevo: "Modulo:EDITOR" o "Modulo:VISOR"
 * - Formato legacy: "Modulo" (sin permiso, hereda del rol)
 */
export const parseModulePermission = (entry: string): { module: string; permission: 'EDITOR' | 'VISOR' } => {
    if (!entry) return { module: '', permission: 'VISOR' };
    if (entry.includes(':')) {
        const [mod, perm] = entry.split(':');
        return { module: mod, permission: perm.toUpperCase() === 'EDITOR' ? 'EDITOR' : 'VISOR' };
    }
    return { module: entry, permission: 'EDITOR' }; // Legacy: hereda como editor para compatibilidad
};

export const getModulesForRole = (role: string): string[] => {
    const normalizedRole = role?.toUpperCase();
    if (normalizedRole === 'SUPER_ADMIN' || normalizedRole === 'ADMINISTRADOR' || normalizedRole === 'DIRECTOR_NACIONAL') {
        return ALL_MODULES.map(m => `${m}:EDITOR`);
    }
    if (normalizedRole === 'EDITOR') return RESTRICTED_FALLBACK_MODULES.map(m => `${m}:EDITOR`);
    if (
        normalizedRole === 'VICE_PRESIDENT' ||
        normalizedRole === 'VICEPRESIDENTE' ||
        normalizedRole === 'COORDINADOR' ||
        normalizedRole === 'COMMISSION_PRESIDENT' ||
        normalizedRole === 'COMMITTEE_PRESIDENT' ||
        normalizedRole === 'PRESIDENTE' ||
        normalizedRole === 'PRESIDENTE_INSTANCIA' ||
        normalizedRole === 'INSTANCIA_PRESIDENTE'
    ) {
        return RESTRICTED_FALLBACK_MODULES.map(m => `${m}:VISOR`);
    }

    if (normalizedRole === 'VISOR' || normalizedRole === 'VIEWER' || normalizedRole === 'VIEW_ONLY') {
        return RESTRICTED_FALLBACK_MODULES.map(m => `${m}:VISOR`);
    }

    return RESTRICTED_FALLBACK_MODULES.map(m => `${m}:VISOR`);
};
export const VISOR_ALLOWED_MODULES = [
    'TRACKING_INSTANCE_DASHBOARD',
    'TRACKING_CAPTURE',
    'TRACKING_REPORT',
    'DASHBOARD',
    'SEGUIMIENTO',
    'REPORTES'
];

/** ¿El usuario puede ACCEDER a este modulo o tab? */
export const canAccessModule = (u: any, moduleKey: string): boolean => {
    if (!u) return false;
    
    const role = u.role?.toUpperCase();
    
    // Super Admin tiene acceso total
    if (role === 'SUPER_ADMIN' || role === 'ADMINISTRADOR') return true;
    
    const key = normalizeModuleName(moduleKey);
    const baseModule = key.startsWith('tracking_') ? 'seguimiento' : key;
    
    // REGLA CANÃ“NICA v18.12.61: Uso obligatorio de safeModuleEquals
    const hasExplicitModules = Array.isArray(u.accessible_modules) && u.accessible_modules.length > 0;
    
    if (hasExplicitModules) {
        return u.accessible_modules.some((entry: string) => {
            const parsed = parseModulePermission(entry);
            return safeModuleEquals(parsed.module, baseModule);
        });
    }
    
    // FALLBACK: Sin módulos explícitos, usar whitelist por rol
    if (role === 'VISOR' || role === 'VIEWER' || role === 'VIEW_ONLY') {
        const normalizedVisorAllowed = VISOR_ALLOWED_MODULES.map(m => normalizeModuleName(m));
        return normalizedVisorAllowed.includes(normalizeModuleName(key));
    }
    
    // Para otros roles sin config explícita, usar módulos por defecto del rol
    const myModules = getUserModules(u);
    return myModules.some(m => safeModuleEquals(m, baseModule));
};


/** Obtener lista de modulos visibles para un usuario */
export const getUserModules = (u: any): string[] => {
    if (!u) return [];
    if (u?.role === 'SUPER_ADMIN') return ALL_MODULES;
    // REGLA v18.10.89: accessible_modules explícitos son la fuente canónica.
    // Solo caer al fallback de rol si NO hay módulos configurados.
    const hasExplicit = Array.isArray(u?.accessible_modules) && u.accessible_modules.length > 0;
    const raw = hasExplicit ? u.accessible_modules : getModulesForRole(u?.role || 'VISOR');
    return raw.map((entry: string) => normalize(parseModulePermission(entry).module));
};

/** ¿El usuario puede EDITAR en este modulo? */
export const canEditModule = (u: any, moduleName: string): boolean => {
    if (!u) return false;
    if (u.role === 'SUPER_ADMIN' || u.role === 'ADMINISTRADOR') return true;

    const raw = (u.accessible_modules && u.accessible_modules.length > 0) ? u.accessible_modules : getModulesForRole(u.role || 'VISOR');
    const entry = raw.find((e: string) => normalize(parseModulePermission(e).module) === normalize(moduleName));
    
    if (!entry) return false;
    return parseModulePermission(entry).permission === 'EDITOR';
};

export const getModulePermission = (u: any, moduleName: string): 'EDITOR' | 'VISOR' | null => {
    if (!u) return null;
    if (u.role === 'SUPER_ADMIN' || u.role === 'ADMINISTRADOR') return 'EDITOR';
    const raw = (u.accessible_modules && u.accessible_modules.length > 0) ? u.accessible_modules : getModulesForRole(u.role || 'VISOR');
    const entry = raw.find((e: string) => normalize(parseModulePermission(e).module) === normalize(moduleName));
    if (!entry) return null;
    return parseModulePermission(entry).permission;
};

export const calculateProratedCompliance = (
  indicadores: any[],
  periodIdx: number,
  useCurrentPeriodOnly: boolean = false,
  strictMonthEligibility: boolean = false
 ): { 
   cumplimiento: number | null; 
   coverage: number; 
   confidence_score: number;
   activeWeight: number;
   configuredWeight: number;
   activeCount: number;
   totalMeta: number;
   totalReal: number;
   audit: any[];
 } => {
  if (!indicadores || !Array.isArray(indicadores) || indicadores.length === 0) {
    return { cumplimiento: 0, coverage: 0, confidence_score: 0, activeWeight: 0, configuredWeight: 0, activeCount: 0, totalMeta: 0, totalReal: 0, audit: [] };
  }

  let totalWeightedPct = 0;
  let totalWeightActive = 0;
  let totalWeightConfigured = 0;
  let totalMeta = 0;
  let totalReal = 0;
  const audit: any[] = [];

  indicadores.forEach(ind => {
    const weight = parseNumberSafe(ind.peso, 0);
    totalWeightConfigured += weight;

    const metrics = getIndicatorMetrics(ind, periodIdx, useCurrentPeriodOnly);
    
    // REGLA CANÃ“NICA v18.9.119:
    // Un indicador es activo para el prorrateo si getIndicatorMetrics determina que tiene metas en el periodo/mes evaluado.
    const isActive = metrics.isActive;
    
    if (isActive) {
      totalWeightActive += weight;
      totalWeightedPct += (metrics.pct * weight);
      totalMeta += metrics.meta;
      totalReal += metrics.real;
      
      audit.push({
        id: ind.id,
        name: ind.name,
        peso: weight,
        metaEvaluada: metrics.meta,
        realEvaluado: metrics.real,
        pct: metrics.pct,
        isActive: true,
        activeReason: `Activo en periodo/mes`,
        contribution: weight > 0 ? (metrics.pct * weight) : 0
      });
    } else {
      audit.push({
        id: ind.id,
        name: ind.name,
        peso: weight,
        metaEvaluada: metrics.meta,
        realEvaluado: metrics.real,
        pct: metrics.pct,
        isActive: false,
        activeReason: `Excluido: Sin metas en periodo/mes`,
        contribution: 0
      });
    }
  });

  const activeCount = audit.filter(a => a.isActive).length;
  const coverage = indicadores.length > 0 ? Math.round((activeCount / indicadores.length) * 100) : 0;
  
  // REGLA: El cumplimiento se calcula solo sobre los activos. 
  // Si hay indicadores pero ninguno es activo (ej. meta 0), devolvemos null para evitar heredar periodos anteriores
  // y para indicar a las vistas consolidadas que esta instancia no debe promediarse.
  const cumplimiento = totalWeightActive > 0 ? Math.round(totalWeightedPct / totalWeightActive) : null;
  const confidence_score = totalWeightConfigured > 0 ? Math.round((totalWeightActive / totalWeightConfigured) * 100) : 0;

  // Enriquecer auditoría con pesos normalizados y contribuciones reales al cumplimiento
  const enrichedAudit = audit.map(a => {
    if (!a.isActive) return { ...a, normalizedWeight: '0.00%', contribution: '0.00' };
    const normalizedWeight = totalWeightActive > 0 ? (a.peso / totalWeightActive) : 0;
    const contribution = a.pct * normalizedWeight;
    return {
      ...a,
      normalizedWeight: (normalizedWeight * 100).toFixed(2) + '%',
      contribution: contribution.toFixed(2)
    };
  });

  return { 
    cumplimiento, 
    coverage, 
    confidence_score, 
    activeWeight: totalWeightActive, 
    configuredWeight: totalWeightConfigured,
    activeCount,
    totalMeta,
    totalReal,
    audit: enrichedAudit
  };
};

export const countActiveYTD = (indicadores: any[], periodIdx: number, useCurrentPeriodOnly: boolean = false) => {
  if (!indicadores || !Array.isArray(indicadores)) return 0;
  return indicadores.filter(ind => getIndicatorMetrics(ind, periodIdx, useCurrentPeriodOnly).isActive).length;
};


export const normalizeInstanceType = (type: any): string => {
  const t = String(type || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase();

  if (t.includes('COMISION')) return 'COMISION';
  if (t.includes('COMIT')) return 'COMITE';
  if (t.includes('CONSEJ')) return 'CONSEJERIA';

  return 'COMISION';
};

export const normalizeVPType = (type: any): string => {
  return normalizeInstanceType(type);
};

export const getConfiguredNumber = (settings: any, key: string, fallback: number): number => {
  const n = Number(settings?.[key]);
  return Number.isFinite(n) ? n : fallback;
};

export const getTypeWeight = (type: any, settings: any): number => {
  const normalized = normalizeVPType(type);

  if (normalized === 'COMISION') {
    return getConfiguredNumber(settings, 'weight_comisiones', DEFAULT_SETTINGS.weight_comisiones);
  }

  if (normalized === 'COMITE') {
    return getConfiguredNumber(settings, 'weight_comites', DEFAULT_SETTINGS.weight_comites);
  }

  if (normalized === 'CONSEJERIA') {
    return getConfiguredNumber(settings, 'weight_consejerias', DEFAULT_SETTINGS.weight_consejerias);
  }

  return 0;
};

/**
 * Contrato canónico de Seguimiento para ponderar una VP.
 * calculateVPPerformance reparte el peso configurado de cada tipo entre sus
 * instancias; multiplicarlo previamente por la población conserva el peso
 * relativo 50/30/20 de cada instancia, tal como lo presenta Seguimiento.
 */
export const buildCanonicalVpPerformanceSettings = (instances: any[], settings: any) => {
  const counts = (Array.isArray(instances) ? instances : [])
    .filter(isActiveVPInstance)
    .reduce((acc: Record<string, number>, inst: any) => {
      const type = normalizeVPType(inst?.type || inst?.commission_type);
      if (type === 'COMISION' || type === 'COMITE' || type === 'CONSEJERIA') {
        acc[type] = (acc[type] || 0) + 1;
      }
      return acc;
    }, { COMISION: 0, COMITE: 0, CONSEJERIA: 0 });

  return {
    ...settings,
    weight_comisiones: getConfiguredNumber(settings, 'weight_comisiones', 50) * Math.max(counts.COMISION, 1),
    weight_comites: getConfiguredNumber(settings, 'weight_comites', 30) * Math.max(counts.COMITE, 1),
    weight_consejerias: getConfiguredNumber(settings, 'weight_consejerias', 20) * Math.max(counts.CONSEJERIA, 1)
  };
};

const isActiveVPInstance = (inst: any): boolean => {
  if (!inst) return false;

  const status = String(inst.status || inst.estado || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase();

  if (inst.active === false) return false;
  if (inst.is_active === false) return false;
  if (status === 'INACTIVO') return false;
  if (status === 'INACTIVE') return false;
  if (status === 'ARCHIVED') return false;
  if (status === 'ELIMINADO') return false;

  return true;
};

const hasApplicablePlanDimension = (inst: any): boolean => {
  if (!inst) return false;
  if (inst.planEligible === true || inst.hasActivePlan === true) return true;

  return parseNumberSafe(
    inst.planGoal ?? inst.total_goal ?? inst.totalGoal ?? inst.total_meta_plan ?? inst.totalMetaPlan,
    0
  ) > 0;
};

const hasApplicableKpiDimension = (inst: any): boolean => {
  if (!inst) return false;
  if (inst.kpiEligible === true || inst.hasActiveKpis === true) return true;

  const activeKpiCount = parseNumberSafe(
    inst.activeKpis ?? inst.kpiCount ?? inst.kpi_count ?? inst.total_indicators ?? inst.totalIndicators,
    0
  );
  const kpiGoal = parseNumberSafe(
    inst.kpiMeta ?? inst.totalMeta ?? inst.total_meta ?? inst.meta_anual ?? inst.total_meta_kpi,
    0
  );
  const kpiReal = parseNumberSafe(
    inst.kpiReal ?? inst.total_real ?? inst.real_anual ?? inst.kpi_progress,
    0
  );

  return kpiGoal > 0 && (activeKpiCount > 0 || kpiReal > 0);
};

export const calculateVPPerformance = (instances: any[], settings: any) => {
  try {
    const safeInstances = (Array.isArray(instances) ? instances : []).filter(inst => {
      return isActiveVPInstance(inst);
    });
    
    // 1. Identificar tipos existentes
    const typesFound = Array.from(new Set(safeInstances.map(i => normalizeVPType(i.type || i.commission_type))));
    
    // 2. Obtener pesos base para cada tipo
    const typeBaseWeights: Record<string, number> = {};
    let activeBaseTotal = 0;
    
    typesFound.forEach(t => {
      const w = t === 'COMISION' ? getConfiguredNumber(settings, 'weight_comisiones', 50) :
                t === 'COMITE' ? getConfiguredNumber(settings, 'weight_comites', 30) :
                t === 'CONSEJERIA' ? getConfiguredNumber(settings, 'weight_consejerias', 20) : 0;
      typeBaseWeights[t] = w;
      activeBaseTotal += w;
    });

    // 3. Normalizar pesos por tipo y calcular peso por instancia
    const instanceWeights: Record<string, number> = {};
    const auditInstances: any[] = [];

    typesFound.forEach(t => {
      const typeInstances = safeInstances.filter(i => normalizeVPType(i.type || i.commission_type) === t);
      if (typeInstances.length === 0) return;

      const normalizedTypeWeight = activeBaseTotal > 0 ? (typeBaseWeights[t] / activeBaseTotal) : 0;
      const weightPerInstance = normalizedTypeWeight / typeInstances.length;

      typeInstances.forEach(inst => {
        instanceWeights[inst.id] = weightPerInstance;
      });
    });

    // 4. Calcular dimensiones con PRORRATEO (v18.9.119 GOLD)
    // Solo consideramos instancias que tienen data activa para esa dimensión
    let kpiWeightedSum = 0;
    let kpiTotalWeight = 0;
    let planWeightedSum = 0;
    let planTotalWeight = 0;

    safeInstances.forEach(inst => {
      const weight = instanceWeights[inst.id] || 0;
      const kpiApplicable = hasApplicableKpiDimension(inst);
      const planApplicable = hasApplicablePlanDimension(inst);
      const kpiVal = kpiApplicable ? parseNumberSafe(inst.kpiCompliance, 0) : null;
      const planVal = parseNumberSafe(inst.planProgress, 0);

      // v18.9.121: Inclusión total de población activa para promedio VP (Dilución según regla executive)
      // v18.10.19: Prorrateo por Elegibilidad (Evita dilución por instancias sin metas/indicadores)
      if (isActiveVPInstance(inst)) {
          if (kpiApplicable) {
            kpiWeightedSum += ((kpiVal ?? 0) * weight);
            kpiTotalWeight += weight;
          }
          
          if (planApplicable) {
            planWeightedSum += (planVal * weight);
            planTotalWeight += weight;
          }
      }

      auditInstances.push({
        id: inst.id,
        name: inst.name,
        type: normalizeVPType(inst.type || inst.commission_type),
        weight: (weight * 100).toFixed(4) + '%',
        kpi: kpiVal,
        plan: planVal,
        kpiEligible: kpiApplicable,
        planEligible: planApplicable
      });
    });

    const hasApplicableKpis = kpiTotalWeight > 0;
    const hasApplicablePlans = planTotalWeight > 0;
    const kpiExact = hasApplicableKpis ? (kpiWeightedSum / kpiTotalWeight) : null;
    const planExact = hasApplicablePlans ? (planWeightedSum / planTotalWeight) : null;

    // 5. Global
    const weightDimensionKpis = getConfiguredNumber(settings, 'weight_dimension_kpis', 70);
    const weightDimensionPlans = getConfiguredNumber(settings, 'weight_dimension_plans', 30);
    const applicableDimensionWeight =
      (hasApplicableKpis ? weightDimensionKpis : 0) +
      (hasApplicablePlans ? weightDimensionPlans : 0);

    const globalExact = applicableDimensionWeight > 0
      ? (((kpiExact ?? 0) * weightDimensionKpis) + ((planExact ?? 0) * weightDimensionPlans)) / applicableDimensionWeight
      : null;



    return {
      global: globalExact === null ? null : Math.round(globalExact),
      kpi: kpiExact === null ? null : Math.round(kpiExact),
      plan: planExact === null ? null : Math.round(planExact),
      kpiExact,
      planExact,
      globalExact,
      hasApplicableKpis,
      hasApplicablePlans,
      hasApplicableData: applicableDimensionWeight > 0,
      applicableDimensionWeight,
      auditInstances,
      activePlans: safeInstances.filter(hasApplicablePlanDimension).length,
      settings: {
        weightDimensionKpis,
        weightDimensionPlans,
        typeBaseWeights,
        activeBaseTotal,
        applicableDimensionWeight
      }
    };
  } catch (error) {
    console.error('Error in calculateVPPerformance:', error);
    return {
      global: null,
      kpi: null,
      plan: null,
      kpiExact: null,
      planExact: null,
      globalExact: null,
      hasApplicableKpis: false,
      hasApplicablePlans: false,
      hasApplicableData: false,
      applicableDimensionWeight: 0,
      auditInstances: []
    };
  }
};

export const TRACKING_METRICS_HARDLOCK = Object.freeze({
  version: 'v18.19.0-OPERATIONAL-CONTROL-MVP',
  guardedFunctions: Object.freeze([
    'getIndicatorMetrics',
    'calculateProratedCompliance',
    'countActiveYTD',
    'calculateVPPerformance',
    'normalizeInstanceType',
    'normalizeVPType',
    'getElapsedCompletedMonths'
  ])
});

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// ðŸš§ MOTOR VISUAL CANÃ“NICO â€” COMPLIANCE ENGINE v18.18.6
// ESTABLE â€” NO MODIFICAR SIN VALIDACIÃ“N VISUAL COMPLETA
// Fuente única de verdad para toda la semaforización de la plataforma.
// Reemplaza: getSemaphoreColor(), getSemaphoreBg(), inline threshold conditionals.
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

export interface ComplianceVisualState {
  /** Clase Tailwind de color de texto (e.g. 'text-emerald-500') */
  textClass: string;
  /** Clase Tailwind de color de texto suave (e.g. 'text-emerald-400') */
  textSoftClass: string;
  /** Clase Tailwind de fondo (e.g. 'bg-emerald-500') */
  bgClass: string;
  /** Clase Tailwind de fondo suave (e.g. 'bg-emerald-500/20') */
  bgSoftClass: string;
  /** Clase Tailwind de borde (e.g. 'border-emerald-500/30') */
  borderClass: string;
  /** Estado semántico canónico */
  semanticState: 'green' | 'amber' | 'red';
  /** Etiqueta legible para UI */
  labelState: 'ÓPTIMO' | 'ESTABLE' | 'CRÍTICO';
  /** Color hex para SVG/Canvas (recharts, etc.) */
  hexColor: string;
}

/**
 * getComplianceVisualState â€” Motor Visual Canónico
 *
 * @param value - Valor numérico de cumplimiento (0-100)
 * @param settings - Objeto de configuración del sistema (threshold_green, threshold_amber)
 * @returns ComplianceVisualState con todas las clases y estado semántico
 *
 * @example
 * const vs = getComplianceVisualState(8, settings);
 * // vs.textClass   â†’ 'text-red-500'
 * // vs.bgClass     â†’ 'bg-red-500'
 * // vs.semanticState â†’ 'red'
 * // vs.labelState  â†’ 'CRÍTICO'
 */
export const getComplianceVisualState = (
  value: number,
  settings?: { threshold_green?: number; threshold_amber?: number } | null
): ComplianceVisualState => {
  const tGreen = settings?.threshold_green ?? 90;
  const tAmber = settings?.threshold_amber ?? 75;

  if (value >= tGreen) {
    return {
      textClass:    'text-emerald-500',
      textSoftClass:'text-emerald-400',
      bgClass:      'bg-emerald-500',
      bgSoftClass:  'bg-emerald-500/20',
      borderClass:  'border-emerald-500/30',
      semanticState:'green',
      labelState:   'ÓPTIMO',
      hexColor:     '#10b981',
    };
  }

  if (value >= tAmber) {
    return {
      textClass:    'text-amber-500',
      textSoftClass:'text-amber-400',
      bgClass:      'bg-amber-500',
      bgSoftClass:  'bg-amber-500/20',
      borderClass:  'border-amber-500/30',
      semanticState:'amber',
      labelState:   'ESTABLE',
      hexColor:     '#f59e0b',
    };
  }

  return {
    textClass:    'text-red-500',
    textSoftClass:'text-red-400',
    bgClass:      'bg-red-500',
    bgSoftClass:  'bg-red-500/20',
    borderClass:  'border-red-500/30',
    semanticState:'red',
    labelState:   'CRÍTICO',
    hexColor:     '#ef4444',
  };
};

// â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• â• 
// ðŸ”’ COMPLIANCE ENGINE HARDLOCK â€” NO MODIFICAR FUNCIONES GUARDED
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
export const COMPLIANCE_ENGINE_HARDLOCK = Object.freeze({
  version: 'v18.18.6-FINAL-SEMAPHORE-HARDENED',
  guardedFunctions: Object.freeze([
    'getComplianceVisualState',
  ])
});












